import useSWR from "swr";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { MapContainer, TileLayer, CircleMarker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
  ArrowRight,
  Activity,
  BarChart3,
  Map as MapIcon,
  CheckCircle2,
  XCircle,
  AlertCircle,
} from "lucide-react";
import { fetcher } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import FleetStatusWidget from "@/components/dashboard/FleetStatusWidget";
import SigeusStatusWidget from "@/components/dashboard/SigeusStatusWidget";
import "@/i18n/fase2";
import "@/i18n/fase3";

function CarbonKpi() {
  const { t } = useTranslation();
  const { data } = useSWR("/carbon/baseline", fetcher);
  const delta = data?.delta_kgco2e;
  const valueT = delta != null ? (Math.abs(delta) / 1000).toFixed(1) : "—";
  return (
    <Link to="/app/pegada" className="block" data-testid="kpi-co2e-link">
      <div
        data-testid="kpi-co2e"
        className="rounded-2xl border border-salnes-atlantic p-6 bg-salnes-atlantic text-white min-h-[128px] flex flex-col justify-between hover:brightness-110 transition"
      >
        <div className="text-[10px] uppercase tracking-[0.22em] opacity-80">
          {t("fase3.co2e_avoided")}
        </div>
        <div className="font-serif text-4xl leading-none tabular-nums">
          {delta != null && delta < 0 ? "-" : ""}
          {valueT}
          <span className="text-sm ml-1 opacity-70">t</span>
        </div>
      </div>
    </Link>
  );
}

function Kpi({ label, value, tone = "atlantic", testid }) {
  const tones = {
    atlantic: "bg-white text-salnes-ink border-salnes-stone/20",
    emerald: "bg-salnes-emerald text-white border-salnes-emerald",
    coral: "bg-salnes-coral text-white border-salnes-coral",
    sand: "bg-salnes-sand/60 text-salnes-ink border-salnes-sand",
  };
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border p-6 flex flex-col justify-between min-h-[128px] ${tones[tone]}`}
    >
      <div className="text-[10px] uppercase tracking-[0.22em] opacity-80">
        {label}
      </div>
      <div className="font-serif text-5xl leading-none tabular-nums">
        {value ?? "—"}
      </div>
    </div>
  );
}

function SourceHealthWidget() {
  const { t } = useTranslation();
  const { data } = useSWR("/salnes/data-sources/health", fetcher, {
    refreshInterval: 60000,
  });
  const sources = data?.sources || [];
  return (
    <div
      className="rounded-2xl border hairline bg-white p-6"
      data-testid="data-health-widget"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="text-[11px] uppercase tracking-[0.22em] text-salnes-stone">
          {t("fase2.data_health")}
        </div>
        <div className="text-xs text-salnes-stone">
          {data?.summary?.healthy || 0}/{data?.summary?.total || 0}
        </div>
      </div>
      <div className="space-y-2 text-sm">
        {sources.map((s) => (
          <div
            key={s.code}
            data-testid={`health-source-${s.code}`}
            className="flex items-center justify-between border-b hairline pb-2 last:border-none"
          >
            <div className="flex items-center gap-2">
              {s.healthy === true ? (
                <CheckCircle2 size={14} className="text-salnes-emerald" />
              ) : s.healthy === false ? (
                <XCircle size={14} className="text-salnes-coral" />
              ) : (
                <AlertCircle size={14} className="text-salnes-stone" />
              )}
              <span>{s.name_gl}</span>
            </div>
            <span className="text-[11px] text-salnes-stone tabular-nums">
              {s.latency_ms != null ? `${s.latency_ms} ms` : "—"}
            </span>
          </div>
        ))}
        {sources.length === 0 && (
          <div className="text-salnes-stone text-xs">…</div>
        )}
      </div>
    </div>
  );
}

function MiniMap() {
  const { t } = useTranslation();
  const { data } = useSWR("/salnes/map-points?limit=50", fetcher);
  const points = (data?.points || []).slice(0, 20);
  return (
    <div
      className="rounded-2xl border hairline bg-white p-4 relative overflow-hidden"
      data-testid="mini-map-widget"
    >
      <div className="flex items-center justify-between mb-3 px-2">
        <div className="text-[11px] uppercase tracking-[0.22em] text-salnes-stone">
          {t("fase2.geoportal_title")}
        </div>
        <Link
          to="/app/geoportal"
          className="text-xs text-salnes-atlantic hover:underline flex items-center gap-1"
          data-testid="mini-map-cta"
        >
          {t("fase2.view_geoportal")} <ArrowRight size={12} />
        </Link>
      </div>
      <div className="h-56 rounded-xl overflow-hidden">
        <MapContainer
          center={[42.5, -8.82]}
          zoom={10}
          scrollWheelZoom={false}
          zoomControl={false}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            attribution=""
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          />
          {points.map((p) => (
            <CircleMarker
              key={p.id}
              center={[p.lat, p.lon]}
              radius={4}
              pathOptions={{
                color: p.overflow ? "#E76F51" : "#0B4C7A",
                fillColor: p.overflow ? "#E76F51" : "#10B981",
                fillOpacity: 0.85,
                weight: 1.5,
              }}
            />
          ))}
        </MapContainer>
      </div>
    </div>
  );
}

function LastDetections() {
  const { t } = useTranslation();
  const { data } = useSWR("/salnes/detections?limit=5", fetcher);
  const list = data?.detections || [];
  return (
    <div
      className="rounded-2xl border hairline bg-white p-6"
      data-testid="last-detections-widget"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="text-[11px] uppercase tracking-[0.22em] text-salnes-stone">
          {t("fase2.last_detections")}
        </div>
        <Link
          to="/app/geoportal"
          className="text-xs text-salnes-atlantic hover:underline"
          data-testid="last-detections-cta"
        >
          {t("fase2.view_geoportal")}
        </Link>
      </div>
      <div className="divide-y hairline">
        {list.map((d) => (
          <div
            key={d.object_id}
            className="flex items-center justify-between py-2 text-sm"
          >
            <div className="min-w-0">
              <div className="text-salnes-ink font-medium">#{d.object_id}</div>
              <div className="text-[11px] text-salnes-stone truncate">
                {(d.detection_date || "").slice(0, 16).replace("T", " · ")}
              </div>
            </div>
            <div className="text-[11px] text-salnes-stone">
              {d.workflow_status}
            </div>
          </div>
        ))}
        {list.length === 0 && (
          <div className="text-salnes-stone text-xs py-4">
            {t("fase2.empty_state")}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: kpis } = useSWR("/salnes/kpis", fetcher, {
    refreshInterval: 60000,
  });
  const { data: tt } = useSWR("/integrations/tristrash/health", fetcher, {
    refreshInterval: 60000,
  });

  const u = kpis?.upstream || {};
  const d = kpis?.derived || {};

  return (
    <div className="max-w-[1400px]" data-testid="dashboard-root">
      <div className="text-[11px] uppercase tracking-[0.28em] text-salnes-emerald mb-2">
        {t("dashboard.welcome")}, {user?.name}
      </div>
      <h1 className="font-serif text-4xl lg:text-5xl text-salnes-ink leading-tight">
        Salnés — {t("fase2.geoportal_title")}
      </h1>
      <p className="mt-3 text-salnes-stone max-w-2xl">
        {t("fase2.geoportal_subtitle")}
      </p>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mt-8 grid grid-cols-2 lg:grid-cols-5 gap-4"
      >
        <Kpi label={t("fase2.detection") + "s"} value={u.total_detections} testid="kpi-detections" />
        <Kpi label={t("fase2.status_resolved")} value={d.resolved_count} tone="emerald" testid="kpi-resolved" />
        <Kpi label={t("fase2.observation") + "s"} value={u.observations_count} tone="sand" testid="kpi-observations" />
        <Kpi label={t("fase2.overflow") + "s"} value={u.total_detections ? Math.max(0, u.total_detections - d.resolved_count) : 0} tone="coral" testid="kpi-overflow" />
        <CarbonKpi />
      </motion.div>

      <div className="mt-8 grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <MiniMap />
          <LastDetections />
        </div>
        <div className="space-y-4">
          <FleetStatusWidget />
          <SigeusStatusWidget />
          <SourceHealthWidget />
          <div className="rounded-2xl border hairline bg-salnes-atlantic text-white p-6">
            <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-[0.22em] text-white/60">
              <Activity size={13} /> {t("dashboard.integration")}
            </div>
            <div className="font-serif text-lg">
              {tt?.status === "ok"
                ? t("dashboard.integration_ok")
                : t("dashboard.integration_error")}
            </div>
            <div className="mt-1 text-xs text-white/70">
              {tt?.upstream?.replace("https://", "")} · {tt?.latency_ms} ms
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <Link
                to="/app/geoportal"
                className="rounded-full bg-white/10 hover:bg-white/20 px-3 py-1.5 text-[12px] flex items-center justify-between transition-colors"
                data-testid="dashboard-cta-geoportal"
              >
                <span className="flex items-center gap-1.5">
                  <MapIcon size={12} /> {t("fase2.nav_geoportal")}
                </span>
                <ArrowRight size={12} />
              </Link>
              <Link
                to="/app/esg"
                className="rounded-full bg-white/10 hover:bg-white/20 px-3 py-1.5 text-[12px] flex items-center justify-between transition-colors"
                data-testid="dashboard-cta-esg"
              >
                <span className="flex items-center gap-1.5">
                  <BarChart3 size={12} /> {t("fase2.nav_esg")}
                </span>
                <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
